import { createFileRoute } from "@tanstack/react-router";
import {
  BLOCKING_PARALLEL_ELEVATION_HIGH,
  BLOCKING_PARALLEL_ELEVATION_LOW,
} from "building-generation/elements/structural-framing/wall/wallFramingBlocking";
import { generateWallFramingWithConvexSplit } from "building-generation/elements/structural-framing/wall/wallFramingConvexSplit";
import {
  WallFramingScenes,
  WallFramingTestCases,
} from "building-generation/elements/structural-framing/wall/wallFramingTestData";
import type { WallFramingScene } from "building-generation/elements/structural-framing/wall/wallFramingTestData";
import { filterWallFramingInputOpenings } from "building-generation/generationTools/shouldSkipWallOpeningFraming";
import { LumberProfileType } from "building-types/framing/lumberTypes";
import type { Framing, GeneratedFramedWallAssembly, WallFramingInput } from "building-types/framing/wallFramingTypes";
import type { WallBlockingHeightMode } from "building-types/walls/wallFramingMetaDataTypes";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  debugFramingDescriptorFromLumberProfile,
  debugLumberProfileFromDescriptor,
} from "../../../components/debug/floor-framing/components/debugFramingDescriptors";
import { createDefaultWallOpeningFramingConfigGroup } from "../../../components/debug/wall-framing/components/defaultWallOpeningFramingConfig";
import { FramingParametersPanel } from "../../../components/debug/wall-framing/components/FramingParametersPanel";
import { InputDataEditor } from "../../../components/debug/wall-framing/components/InputDataEditor";
import { OpeningFramingConfigsPanel } from "../../../components/debug/wall-framing/components/OpeningFramingConfigsPanel";
import { SaveLoadControls } from "../../../components/debug/wall-framing/components/SaveLoadControls";
import {
  CUSTOM_WALL_VALUE,
  TestCaseSelector,
} from "../../../components/debug/wall-framing/components/TestCaseSelector";
import { View3D } from "../../../components/debug/wall-framing/components/View3D";
import {
  cloneWallFramingInput,
  computeWallFramingStartPt,
  createDefaultWallInput,
  DEFAULT_WALL_HEIGHT_INCHES,
  DEFAULT_WALL_STUD_DIRECTION,
  DEFAULT_WALL_WIDTH_INCHES,
  mergeWallInputWithPanelState,
  readWallDimensionsFromInput,
  updateWallDimensions,
} from "../../../components/debug/wall-framing/components/wallFramingInputUtils";

const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = MIN_PANEL_WIDTH * 2;
const STORAGE_KEY = "wallFramingTestCases";

interface SavedTestCase {
  name: string;
  timestamp: string;
  input: WallFramingInput;
}

/** Generate every wall in a scene and concatenate their framing into a single combined assembly. */
function generateSceneFraming(scene: WallFramingScene): GeneratedFramedWallAssembly {
  const framing: Framing[] = [];
  const errors: string[] = [];
  for (const wall of scene.walls) {
    const assembly = generateWallFramingWithConvexSplit(wall.input, {});
    framing.push(...assembly.framing);
    if (assembly.openingFramingErrors) {
      errors.push(...assembly.openingFramingErrors);
    }
  }
  return {
    levelId: "debug-scene",
    framing,
    openingFramingErrors: errors.length > 0 ? errors : undefined,
  };
}

function WallFramingTestPage() {
  const [panelWidth, setPanelWidth] = useState<number>(MIN_PANEL_WIDTH);
  const [resizing, setResizing] = useState<boolean>(false);
  const [selectedTestCase, setSelectedTestCase] = useState<string>("");
  const [selectedScene, setSelectedScene] = useState<string>("");
  const [currentInput, setCurrentInput] = useState<WallFramingInput | null>(null);
  const [generatedFraming, setGeneratedFraming] = useState<GeneratedFramedWallAssembly | null>(null);
  const [savedTestCases, setSavedTestCases] = useState<SavedTestCase[]>([]);
  const [wallWidth, setWallWidth] = useState<number>(DEFAULT_WALL_WIDTH_INCHES);
  const [wallHeight, setWallHeight] = useState<number>(DEFAULT_WALL_HEIGHT_INCHES);
  const [spacing, setSpacing] = useState<number>(16);
  const [defaultLumberProfile, setDefaultLumberProfile] = useState<LumberProfileType>(LumberProfileType.SPF_2X4);
  const [framingStartPtX, setFramingStartPtX] = useState<number>(0);
  const [framingStartPtY, setFramingStartPtY] = useState<number>(0);
  const [elevation, setElevation] = useState<number>(0);
  const [hasBlocking, setHasBlocking] = useState<boolean>(false);
  const [blockingHeightMode, setBlockingHeightMode] = useState<WallBlockingHeightMode>("alternate");
  const [blockingFixedHeightInches, setBlockingFixedHeightInches] = useState<number>(BLOCKING_PARALLEL_ELEVATION_LOW);
  const [blockingAlternateLowHeightInches, setBlockingAlternateLowHeightInches] = useState<number>(
    BLOCKING_PARALLEL_ELEVATION_LOW,
  );
  const [blockingAlternateHighHeightInches, setBlockingAlternateHighHeightInches] = useState<number>(
    BLOCKING_PARALLEL_ELEVATION_HIGH,
  );
  const [currentView, setCurrentView] = useState<"XY" | "XZ" | "YZ" | "ISO">("ISO");
  const [showDimensions, setShowDimensions] = useState<boolean>(false);
  const [showBoundaries, setShowBoundaries] = useState<boolean>(false);
  const [generationTimeMs, setGenerationTimeMs] = useState<number | null>(null);
  const isGeneratingRef = useRef<boolean>(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSavedTestCases(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error("Failed to load saved test cases:", e);
      }
    }
  }, []);

  useEffect(() => {
    if (!resizing) {
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      setPanelWidth(() => Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, e.clientX)));
    };
    const handleMouseUp = () => setResizing(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  const generateFraming = useCallback((input: WallFramingInput) => {
    if (isGeneratingRef.current) {
      return;
    }
    isGeneratingRef.current = true;
    try {
      const startTime = performance.now();
      const result = generateWallFramingWithConvexSplit(filterWallFramingInputOpenings(input), {});
      const endTime = performance.now();
      setGenerationTimeMs(endTime - startTime);
      setGeneratedFraming(result);
    } catch (error) {
      console.error("Failed to generate wall framing:", error);
      setGeneratedFraming(null);
      setGenerationTimeMs(null);
    } finally {
      isGeneratingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!currentInput) {
      return;
    }
    const { width, height } = readWallDimensionsFromInput(currentInput);
    setWallWidth(width);
    setWallHeight(height);
  }, [currentInput]);

  useEffect(() => {
    if (!currentInput) {
      return;
    }
    const startPt = computeWallFramingStartPt(currentInput.perimeter.polyline, DEFAULT_WALL_STUD_DIRECTION);
    setFramingStartPtX(startPt[0]);
    setFramingStartPtY(startPt[1]);
  }, [currentInput?.perimeter.polyline]);

  useEffect(() => {
    if (currentInput) {
      const inputWithLatestParams: WallFramingInput = mergeWallInputWithPanelState(currentInput, {
        spacing,
        defaultFramingDescriptor: debugFramingDescriptorFromLumberProfile(defaultLumberProfile),
        framingStartPt: [framingStartPtX, framingStartPtY],
        elevation,
        hasBlocking,
        blockingHeightMode,
        blockingFixedHeightInches,
        blockingAlternateLowHeightInches,
        blockingAlternateHighHeightInches,
      });
      generateFraming(inputWithLatestParams);
    }
  }, [
    currentInput,
    spacing,
    defaultLumberProfile,
    framingStartPtX,
    framingStartPtY,
    elevation,
    hasBlocking,
    blockingHeightMode,
    blockingFixedHeightInches,
    blockingAlternateLowHeightInches,
    blockingAlternateHighHeightInches,
    generateFraming,
  ]);

  const loadWallFramingInput = useCallback((input: WallFramingInput) => {
    let clonedInput = cloneWallFramingInput(input);
    if (!clonedInput.openingFramingConfigGroup && (clonedInput.openings?.length ?? 0) > 0) {
      clonedInput = {
        ...clonedInput,
        openingFramingConfigGroup: createDefaultWallOpeningFramingConfigGroup(),
      };
    }

    setCurrentInput(clonedInput);
    setSpacing(clonedInput.framingMetadata.spacing);
    setDefaultLumberProfile(
      debugLumberProfileFromDescriptor(clonedInput.framingMetadata.defaultFramingDescriptor) ??
        LumberProfileType.SPF_2X4,
    );
    const startPt = clonedInput.framingMetadata.framingStartPt;
    setFramingStartPtX(startPt[0]);
    setFramingStartPtY(startPt[1]);
    setElevation(clonedInput.framingMetadata.elevation ?? 0);
    const meta = clonedInput.framingMetadata;
    setHasBlocking(meta.hasBlocking === true);
    setBlockingHeightMode(meta.blockingHeightMode ?? "alternate");
    setBlockingFixedHeightInches(meta.blockingFixedHeightInches ?? BLOCKING_PARALLEL_ELEVATION_LOW);
    setBlockingAlternateLowHeightInches(meta.blockingAlternateLowHeightInches ?? BLOCKING_PARALLEL_ELEVATION_LOW);
    setBlockingAlternateHighHeightInches(meta.blockingAlternateHighHeightInches ?? BLOCKING_PARALLEL_ELEVATION_HIGH);
  }, []);

  const handleWallWidthChange = useCallback(
    (width: number) => {
      setWallWidth(width);
      if (currentInput) {
        const updated = updateWallDimensions(currentInput, width, wallHeight);
        setFramingStartPtX(updated.framingMetadata.framingStartPt[0]);
        setFramingStartPtY(updated.framingMetadata.framingStartPt[1]);
        setCurrentInput(updated);
      }
    },
    [currentInput, wallHeight],
  );

  const handleWallHeightChange = useCallback(
    (height: number) => {
      setWallHeight(height);
      if (currentInput) {
        const updated = updateWallDimensions(currentInput, wallWidth, height);
        setFramingStartPtX(updated.framingMetadata.framingStartPt[0]);
        setFramingStartPtY(updated.framingMetadata.framingStartPt[1]);
        setCurrentInput(updated);
      }
    },
    [currentInput, wallWidth],
  );

  const saveTestCase = useCallback(() => {
    if (!currentInput) {
      return;
    }
    const saved: SavedTestCase = {
      name: `Custom Test Case ${savedTestCases.length + 1}`,
      timestamp: new Date().toISOString(),
      input: currentInput,
    };
    const updated = [...savedTestCases, saved];
    setSavedTestCases(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, [currentInput, savedTestCases]);

  const loadSavedTestCase = useCallback(
    (saved: SavedTestCase) => {
      loadWallFramingInput(saved.input);
    },
    [loadWallFramingInput],
  );

  const deleteSavedTestCase = useCallback(
    (index: number) => {
      const updated = savedTestCases.filter((_, i) => i !== index);
      setSavedTestCases(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    },
    [savedTestCases],
  );

  const exportToFile = useCallback(() => {
    if (!currentInput) {
      return;
    }
    const data: SavedTestCase = {
      name: "Exported Test Case",
      timestamp: new Date().toISOString(),
      input: currentInput,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wall-framing-test-case.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [currentInput]);

  const loadFromFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const parsed = JSON.parse(content) as SavedTestCase;
          if (parsed.input) {
            loadSavedTestCase(parsed);
          }
        } catch (error) {
          console.error("Failed to load file:", error);
          alert("Failed to load test case from file");
        }
      };
      reader.readAsText(file);
    },
    [loadSavedTestCase],
  );

  const handleSceneChange = useCallback((sceneId: string) => {
    setSelectedScene(sceneId);
    if (!sceneId) {
      setGeneratedFraming(null);
      setGenerationTimeMs(null);
      return;
    }
    const scene = WallFramingScenes.find((s) => s.id === sceneId);
    if (!scene) {
      return;
    }
    // Scenes are multi-wall; clear the single-wall selection so the panel-driven effect does not
    // regenerate over the combined assembly.
    setSelectedTestCase("");
    setCurrentInput(null);
    const startTime = performance.now();
    const combined = generateSceneFraming(scene);
    setGenerationTimeMs(performance.now() - startTime);
    setGeneratedFraming(combined);
  }, []);

  const handleTestCaseChange = useCallback(
    (name: string) => {
      setSelectedScene("");
      setSelectedTestCase(name);
      setGeneratedFraming(null);

      if (name === CUSTOM_WALL_VALUE) {
        const customInput = createDefaultWallInput(
          wallWidth,
          wallHeight,
          debugFramingDescriptorFromLumberProfile(defaultLumberProfile),
          {
            spacing,
            framingStartPt: [framingStartPtX, framingStartPtY],
            elevation,
            hasBlocking: hasBlocking ? true : undefined,
            blockingHeightMode: hasBlocking ? blockingHeightMode : undefined,
            blockingFixedHeightInches:
              hasBlocking && blockingHeightMode === "fixed" ? blockingFixedHeightInches : undefined,
            blockingAlternateLowHeightInches:
              hasBlocking && blockingHeightMode === "alternate" ? blockingAlternateLowHeightInches : undefined,
            blockingAlternateHighHeightInches:
              hasBlocking && blockingHeightMode === "alternate" ? blockingAlternateHighHeightInches : undefined,
          },
        );
        setFramingStartPtX(customInput.framingMetadata.framingStartPt[0]);
        setFramingStartPtY(customInput.framingMetadata.framingStartPt[1]);
        setCurrentInput(customInput);
      } else if (name) {
        const testCase = WallFramingTestCases.find((tc) => tc.name === name);
        if (testCase) {
          loadWallFramingInput(testCase.input);
        }
      } else {
        setCurrentInput(null);
      }
    },
    [
      loadWallFramingInput,
      wallWidth,
      wallHeight,
      defaultLumberProfile,
      spacing,
      framingStartPtX,
      framingStartPtY,
      elevation,
      hasBlocking,
      blockingHeightMode,
      blockingFixedHeightInches,
      blockingAlternateLowHeightInches,
      blockingAlternateHighHeightInches,
    ],
  );

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: "Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
      }}
    >
      <div
        style={{
          width: panelWidth,
          minWidth: MIN_PANEL_WIDTH,
          maxWidth: MAX_PANEL_WIDTH,
          padding: 8,
          borderRight: "1px solid #eee",
          boxSizing: "border-box",
          height: "100vh",
          overflowY: "auto",
          fontFamily: "inherit",
        }}
      >
        <h2>WALL FRAMING TEST</h2>

        <TestCaseSelector
          selectedTestCase={selectedTestCase}
          onTestCaseChange={handleTestCaseChange}
          currentInput={currentInput}
        />

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>Multi-Wall Scenes</label>
          <select
            style={{ width: "100%", padding: 4, boxSizing: "border-box" }}
            value={selectedScene}
            onChange={(e) => handleSceneChange(e.target.value)}
          >
            <option value="">Select a scene...</option>
            {WallFramingScenes.map((scene) => (
              <option key={scene.id} value={scene.id}>
                {scene.name} ({scene.id})
              </option>
            ))}
          </select>
          {selectedScene && (
            <div style={{ marginTop: 8, padding: 8, backgroundColor: "#f0f0f0", borderRadius: 4, fontSize: 12 }}>
              <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                {WallFramingScenes.find((s) => s.id === selectedScene)?.name}
              </div>
              <div style={{ color: "#666" }}>{WallFramingScenes.find((s) => s.id === selectedScene)?.description}</div>
              <div style={{ marginTop: 4, fontSize: 11, color: "#888" }}>
                {WallFramingScenes.find((s) => s.id === selectedScene)?.walls.length} walls · panel parameters do not
                apply to scenes
              </div>
            </div>
          )}
        </div>

        <FramingParametersPanel
          wallWidth={wallWidth}
          onWallWidthChange={handleWallWidthChange}
          wallHeight={wallHeight}
          onWallHeightChange={handleWallHeightChange}
          spacing={spacing}
          onSpacingChange={setSpacing}
          defaultLumberProfile={defaultLumberProfile}
          onDefaultLumberProfileChange={setDefaultLumberProfile}
          framingStartPtX={framingStartPtX}
          framingStartPtY={framingStartPtY}
          onFramingStartPtXChange={setFramingStartPtX}
          onFramingStartPtYChange={setFramingStartPtY}
          elevation={elevation}
          onElevationChange={setElevation}
          hasBlocking={hasBlocking}
          onHasBlockingChange={setHasBlocking}
          blockingHeightMode={blockingHeightMode}
          onBlockingHeightModeChange={setBlockingHeightMode}
          blockingFixedHeightInches={blockingFixedHeightInches}
          onBlockingFixedHeightInchesChange={setBlockingFixedHeightInches}
          blockingAlternateLowHeightInches={blockingAlternateLowHeightInches}
          onBlockingAlternateLowHeightInchesChange={setBlockingAlternateLowHeightInches}
          blockingAlternateHighHeightInches={blockingAlternateHighHeightInches}
          onBlockingAlternateHighHeightInchesChange={setBlockingAlternateHighHeightInches}
        />

        <OpeningFramingConfigsPanel currentInput={currentInput} onInputChange={setCurrentInput} />

        <SaveLoadControls
          currentInput={currentInput}
          savedTestCases={savedTestCases}
          onSave={saveTestCase}
          onExport={exportToFile}
          onLoadFromFile={loadFromFile}
          onLoadSaved={loadSavedTestCase}
          onDeleteSaved={deleteSavedTestCase}
        />

        <InputDataEditor
          key={selectedTestCase || "custom-wall"}
          currentInput={currentInput}
          onInputChange={setCurrentInput}
        />
      </div>

      <div
        style={{
          width: 8,
          cursor: "col-resize",
          background: resizing ? "#bdbdbd" : "#e0e0e0",
          zIndex: 100,
          userSelect: "none",
          height: "100vh",
          boxShadow: "0 0 4px #bbb",
          transition: "background 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseDown={() => setResizing(true)}
        title="Drag to resize panel"
      >
        <div
          style={{
            width: 2,
            height: "60%",
            background: "#888",
            borderRadius: 2,
            opacity: 0.5,
          }}
        />
      </div>

      <div style={{ flex: 1, position: "relative", background: "#fafbfc", fontFamily: "inherit" }}>
        <View3D
          key={selectedScene ? `scene-${selectedScene}` : "single-wall-view"}
          panelWidth={panelWidth}
          currentInput={currentInput}
          generatedFraming={generatedFraming}
          currentView={currentView}
          onViewChange={setCurrentView}
          showDimensions={showDimensions}
          onShowDimensionsChange={setShowDimensions}
          showBoundaries={showBoundaries}
          onShowBoundariesChange={setShowBoundaries}
        />

        {generatedFraming?.openingFramingErrors && generatedFraming.openingFramingErrors.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 16,
              left: 16,
              right: 16,
              maxWidth: 480,
              background: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: 4,
              padding: "8px 12px",
              fontSize: 12,
              zIndex: 10,
            }}
          >
            <strong>Opening framing errors:</strong>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {generatedFraming.openingFramingErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {generationTimeMs !== null && (
          <div
            style={{
              position: "absolute",
              bottom: 16,
              right: 16,
              background: "rgba(255, 255, 255, 0.9)",
              border: "1px solid #ccc",
              borderRadius: 4,
              padding: "6px 12px",
              fontSize: 12,
              fontFamily: "monospace",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 10,
            }}
          >
            Generation: <strong>{generationTimeMs.toFixed(2)} ms</strong>
          </div>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/debug/wall-framing/")({
  component: WallFramingTestPage,
});
